import React, { useEffect } from 'react';
import {
    ShieldCheck,
    Layers,
    AlertTriangle,
    Building2,
    FileText,
    Link2,
    BookOpen,
    Bot,
    Film,
    TrendingUp,
    AudioLines,
    Podcast,
    LayoutGrid,
    Workflow,
    Mic,
    ListChecks,
    ClipboardCheck,
    MessageSquare,
    CheckCircle2,
    XCircle,
    Landmark
} from 'lucide-react';

/* Reusable card: icon + heading + subheading, nothing else */
const InfoCard = ({ icon: Icon, heading, subheading, accent = "indigo" }) => {
    const accents = {
        indigo: {
            bg: "bg-indigo-900/10",
            border: "border-indigo-500/30",
            iconBg: "bg-indigo-500/10",
            iconColor: "text-indigo-300"
        },
        rose: {
            bg: "bg-rose-900/10",
            border: "border-rose-500/30",
            iconBg: "bg-rose-500/10",
            iconColor: "text-rose-300"
        },
        emerald: {
            bg: "bg-emerald-900/10",
            border: "border-emerald-500/30",
            iconBg: "bg-emerald-500/10",
            iconColor: "text-emerald-300"
        }
    };
    const a = accents[accent];
    return (
        <div className={`rounded-2xl border ${a.border} ${a.bg} p-6 sm:p-7 flex flex-col`}>
            <div className={`${a.iconBg} ${a.iconColor} w-11 h-11 rounded-xl flex items-center justify-center mb-5`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-base sm:text-lg font-black text-white mb-2 leading-snug">{heading}</h3>
            <p className={`text-xs sm:text-sm font-medium leading-snug ${accent === 'rose' ? 'text-rose-100/70' : 'text-white/55'}`}>{subheading}</p>
        </div>
    );
};

const SectionEyebrow = ({ children, color = "indigo" }) => {
    const colors = {
        indigo: "text-indigo-300/80",
        rose: "text-rose-300/80",
        amber: "text-amber-300/80",
        emerald: "text-emerald-300/80"
    };
    return (
        <div className={`text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-4 ${colors[color]}`}>
            {children}
        </div>
    );
};

export default function ShaktiPersonalBrand() {

    useEffect(() => {
        document.title = "Dr. Shakti Katyal | Content Engine";
        window.scrollTo(0, 0);
    }, []);

    const lanes = [
        { n: '01', h: 'The Financial Freedom Lie series', s: 'Calm-authority clone reels in the register of the references you shared. Broad hook, one myth dismantled per reel, complete in 60 seconds.' },
        { n: '02', h: 'The Million Dollar Nap lane', s: 'The book is the positioning. Bestseller plus the 2026 Best Author award as proof, work less and think bigger as the promise.' },
        { n: '03', h: 'SHAKTI, letter by letter', s: 'A numbered six-part framework series that gets watched to the end and binges the profile. Doubles as your first lead magnet.' },
        { n: '04', h: 'The faceless inner-wealth lane', s: 'Affirmation and inner-stability shorts in the exact style you sent. Your cloned voice, music, no face. Posts on days you never think about content.' },
        { n: '05', h: 'Podcast highlight packages', s: 'Your 10 guest episodes cut with the musical highlights intro you loved, posted across Instagram and YouTube Shorts. The Shakti Show plugs straight into this pipeline at launch.' }
    ];

    const wont = [
        { h: 'Guaranteed returns language, ever', s: 'Finance is a regulated trust business. Every script ships with disclaimers and without promises we cannot stand behind.' },
        { h: 'Sales calls closed for you', s: 'We fill the theatre and route qualified people to your calendar. The conversation and the close stay yours. Clean handoff, clearly marked.' },
        { h: 'A clone you have not approved', s: 'Day-7 clause: if the clone does not pass your bar in week one, the AI lane is dropped and the volume shifts to podcast clips and the faceless lane. Same price.' }
    ];

    const will = [
        { h: '10,000 followers in 90 days, or your money back', s: 'Combined across Instagram, YouTube, Facebook and TikTok. Miss it and you get 100% of the fee back. What we need in return: Week-1 recording, 48-hour approvals, cadence uninterrupted.' },
        { h: 'A content engine that runs without you', s: '60 videos a month on your profile. Two hours of your voice, 25 minutes of video, once.' },
        { h: 'Qualified conversations, tracked as a metric', s: 'Broad hooks grow the audience. Narrow CTAs route your actual ICP into DMs and onto your calendar. Both live on the dashboard.' },
        { h: 'Your calm, protected', s: 'One 30-minute call a month and WhatsApp approvals. You trade and meditate; the engine posts.' }
    ];

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-36 sm:pt-40 pb-20 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Real n Royal</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Private</span>
                </div>
            </header>

            <main className="max-w-[1080px] mx-auto px-6 space-y-28 sm:space-y-40">

                {/* HERO */}
                <section className="text-center pt-12 sm:pt-20">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-10">
                        Confidential · Private Proposal · Dr. Shakti Katyal
                    </div>

                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-2">
                        You made the movie.<br />We fill the theatre.
                    </h1>

                    <p className="text-base sm:text-lg md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed font-light">
                        A done-for-you content engine for the Real n Royal brand.<br className="hidden sm:block" /> Work less. Think bigger. Create lasting wealth. And finally, be seen doing it.
                    </p>
                </section>

                {/* PAIN POINTS */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="rose">The honest read</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Where you are right now.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            The content is not the problem. The distribution is.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-6xl mx-auto">
                        <InfoCard icon={Layers} heading="725 followers carrying a doctorate-level brand" subheading="Recent videos average 40 to 100 plays. Full movie, empty theatre" accent="rose" />
                        <InfoCard icon={AlertTriangle} heading="Three surfaces, three stories" subheading="Website sells business coaching · IG bio says Financial Freedom Designer · the real offer is forex mentorship" accent="rose" />
                        <InfoCard icon={Building2} heading="A company voice on a trust business" subheading="The page says 'our CEO and Founder'. In finance, people buy people" accent="rose" />
                        <InfoCard icon={FileText} heading="18 months of clone attempts, no engine" subheading="Delphi, ElevenLabs, Descript, Opus. Tools bought, system never shipped" accent="rose" />
                        <InfoCard icon={Link2} heading="The bio link is a form, not a funnel" subheading="No qualifying, no booking, no reason to act now" accent="rose" />
                        <InfoCard icon={BookOpen} heading="Real assets sitting idle" subheading="Bestselling book · author award · 10 podcasts · SHAKTI framework. None of it compounding" accent="rose" />
                    </div>
                </section>

                {/* THE TRAJECTORY GRAPH */}
                <section>
                    <div className="text-center mb-10 sm:mb-14">
                        <SectionEyebrow color="indigo">The trajectory</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            90 days from now,<br />two versions of this brand exist.
                        </h2>
                        <p className="text-sm sm:text-base text-white/45 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            @realnroyalofficial today: 725 followers from 57 posts, videos averaging 40 to 100 plays. Your new Dr. Shakti Katyal page starts at zero and passes it inside the first few weeks.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto rounded-3xl border border-white/10 bg-white/[0.03] p-5 sm:p-8">
                        {/* Legend */}
                        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-5">
                            <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#6366f1' }}></span>
                                <span className="text-xs sm:text-sm text-white/70 font-medium">New page, with SocialSEO</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#f43f5e' }}></span>
                                <span className="text-xs sm:text-sm text-white/70 font-medium">Real n Royal, at today's pace</span>
                            </div>
                        </div>

                        <svg viewBox="0 0 720 400" className="w-full h-auto" role="img" aria-label="Follower projection over 90 days. Real n Royal at today's pace: 725 to roughly 800. New Dr. Shakti Katyal page with SocialSEO: zero to 10,000 combined across four platforms.">
                            {/* Gridlines + y labels */}
                            {[{ v: '10,000', y: 20 }, { v: '7,500', y: 100 }, { v: '5,000', y: 180 }, { v: '2,500', y: 260 }, { v: '0', y: 340 }].map(g => (
                                <g key={g.y}>
                                    <line x1="70" y1={g.y} x2="690" y2={g.y} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
                                    <text x="60" y={g.y + 4} textAnchor="end" fontSize="12" fill="rgba(255,255,255,0.35)">{g.v}</text>
                                </g>
                            ))}

                            {/* X labels */}
                            {[{ t: 'Today', x: 70 }, { t: 'Day 30', x: 276.7 }, { t: 'Day 60', x: 483.3 }, { t: 'Day 90', x: 690 }].map(g => (
                                <text key={g.t} x={g.x} y="368" textAnchor="middle" fontSize="12" fill="rgba(255,255,255,0.35)">{g.t}</text>
                            ))}

                            {/* Clone-live marker at Day 7 */}
                            <line x1="118.2" y1="20" x2="118.2" y2="340" stroke="rgba(255,255,255,0.10)" strokeWidth="1" strokeDasharray="3 4" />
                            <text x="118.2" y="388" textAnchor="middle" fontSize="11" fill="rgba(255,255,255,0.35)">Day 7 · clone live</text>

                            {/* Area under the with-us line */}
                            <polygon
                                points="70,340 118.2,335.2 173.3,324 276.7,256.8 380,205.6 483.3,151.2 586.7,87.2 690,20 690,340 70,340"
                                fill="rgba(99,102,241,0.10)"
                            />

                            {/* Current-pace line (near flat) */}
                            <polyline
                                points="70,316.8 276.7,316 483.3,315.2 690,314.4"
                                fill="none" stroke="#f43f5e" strokeWidth="2.5" strokeDasharray="7 5" strokeLinecap="round"
                            />
                            {/* Vertex dots so the flat line reads as a series, not an axis */}
                            {[{ x: 276.7, y: 316 }, { x: 483.3, y: 315.2 }].map(p => (
                                <circle key={p.x} cx={p.x} cy={p.y} r="3.5" fill="#f43f5e" stroke="#0b0b12" strokeWidth="1.5" />
                            ))}
                            {/* On-plot rose label pill */}
                            <rect x="299" y="289" width="152" height="23" rx="11.5" fill="rgba(244,63,94,0.16)" stroke="rgba(244,63,94,0.45)" strokeWidth="1" />
                            <text x="375" y="304.5" textAnchor="middle" fontSize="12" fontWeight="700" fill="#fecdd3">At today's pace</text>
                            <line x1="375" y1="312" x2="375" y2="315.6" stroke="rgba(244,63,94,0.45)" strokeWidth="1" />

                            {/* With-us line */}
                            <polyline
                                points="70,340 118.2,335.2 173.3,324 276.7,256.8 380,205.6 483.3,151.2 586.7,87.2 690,20"
                                fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            />

                            {/* Milestone markers on the with-us line */}
                            {[
                                { x: 70, y: 340, tip: 'Day 0: new page starts at zero' },
                                { x: 276.7, y: 256.8, tip: 'Day 30: ~2,600 · cadence at 2/day' },
                                { x: 483.3, y: 151.2, tip: 'Day 60: ~5,900 · hooks tuned on data' },
                                { x: 690, y: 20, tip: 'Day 90: 10,000 combined · or 100% back' }
                            ].map(p => (
                                <g key={p.x}>
                                    <circle cx={p.x} cy={p.y} r="4.5" fill="#6366f1" stroke="#0b0b12" strokeWidth="2" />
                                    <circle cx={p.x} cy={p.y} r="14" fill="transparent">
                                        <title>{p.tip}</title>
                                    </circle>
                                </g>
                            ))}

                            {/* Endpoint of current-pace line */}
                            <circle cx="690" cy="314.4" r="4.5" fill="#f43f5e" stroke="#0b0b12" strokeWidth="2">
                                <title>Day 90 at today's pace: roughly 800 followers</title>
                            </circle>

                            {/* Direct labels */}
                            <text x="78" y="308" fontSize="13" fontWeight="700" fill="rgba(255,255,255,0.85)">725 today</text>
                            <text x="676" y="14" textAnchor="end" fontSize="14" fontWeight="800" fill="rgba(255,255,255,0.95)">10,000 combined</text>
                            <text x="676" y="302" textAnchor="end" fontSize="12" fill="rgba(255,255,255,0.55)">~800 at today's pace</text>
                        </svg>

                        {/* Accessible table fallback */}
                        <table className="sr-only">
                            <caption>Follower projection over 90 days</caption>
                            <thead><tr><th>Point</th><th>Real n Royal at today's pace</th><th>New page with SocialSEO</th></tr></thead>
                            <tbody>
                                <tr><td>Today</td><td>725</td><td>0</td></tr>
                                <tr><td>Day 30</td><td>~750</td><td>~2,600</td></tr>
                                <tr><td>Day 60</td><td>~775</td><td>~5,900</td></tr>
                                <tr><td>Day 90</td><td>~800</td><td>10,000</td></tr>
                            </tbody>
                        </table>

                        <p className="text-center text-[11px] sm:text-xs text-white/35 mt-4 font-light">
                            The new page starts at zero and overtakes Real n Royal inside the first few weeks. 10,000 is the combined figure across Instagram, YouTube, Facebook and TikTok. Today's pace assumes the current cadence of roughly 8 posts a month continues.
                        </p>
                    </div>
                </section>

                {/* WHAT WE BUILD */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">Our side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we build.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-6xl mx-auto">
                        <InfoCard icon={Bot} heading="An AI clone of you" subheading="2 hours of voice + 25 minutes of video, once. You approve every video before it posts" accent="indigo" />
                        <InfoCard icon={Film} heading="60 videos a month" subheading="2 a day on your personal profile. Volume your calendar could never produce" accent="indigo" />
                        <InfoCard icon={TrendingUp} heading="A hook system built for reach" subheading="Broad hooks, narrow bodies. Financial freedom is a mass topic and we engineer for it" accent="indigo" />
                        <InfoCard icon={AudioLines} heading="The faceless lane" subheading="Affirmation and inner-stability shorts. Your voice, music, no face required" accent="indigo" />
                        <InfoCard icon={Podcast} heading="Podcast clip engine" subheading="10 guest episodes cut with musical highlight intros · Shakti Show ready at launch" accent="indigo" />
                        <InfoCard icon={LayoutGrid} heading="A fresh Dr. Shakti Katyal page" subheading="Built from zero, Real n Royal left untouched · bio rebuilt into a funnel ending in your calendar" accent="indigo" />
                        <InfoCard icon={Workflow} heading="End-to-end production" subheading="Ideation, scripting, editing, posting, captions, comment triage. Done for you" accent="indigo" />
                        <InfoCard icon={ShieldCheck} heading="Live dashboard + compliance" subheading="Notion metrics anytime · no guaranteed-returns language, disclaimers baked in" accent="indigo" />
                    </div>
                </section>

                {/* THE 5 LANES */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The five ideas you asked for</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Five content lanes,<br />built from what you already own.
                        </h2>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {lanes.map((lane) => (
                            <div key={lane.n} className="flex items-start gap-5 sm:gap-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
                                <div className="text-2xl sm:text-3xl font-black text-indigo-400/80 shrink-0 w-10 sm:w-12 tabular-nums">{lane.n}</div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-black text-white mb-1 leading-snug">{lane.h}</h3>
                                    <p className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{lane.s}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* WHAT YOU'LL DO */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">Your side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What you'll do.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            Lighter than anything you have tried in 18 months. The engine should need you less, and it does.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Mic} heading="One recording session" subheading="2 hours of voice + 25 minutes of video in Week 1. That trains the clone" accent="emerald" />
                        <InfoCard icon={ListChecks} heading="Week-1 questionnaire" subheading="Your stories, your ICP language, banned words, disclaimers" accent="emerald" />
                        <InfoCard icon={ClipboardCheck} heading="48-hour approvals" subheading="Final videos land on WhatsApp. Minutes a day, not hours" accent="emerald" />
                        <InfoCard icon={MessageSquare} heading="One 30-minute call a month" subheading="Your only standing meeting commitment" accent="emerald" />
                    </div>
                </section>

                {/* PROMISE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">The exchange</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we promise.<br />And what we won't.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl mx-auto">
                        {/* WON'T */}
                        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 sm:p-9">
                            <div className="text-rose-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we won't promise</div>
                            <ul className="space-y-6">
                                {wont.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <XCircle className="w-5 h-5 text-rose-400/70 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/50 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* WILL */}
                        <div className="rounded-3xl border border-indigo-500/30 bg-indigo-900/10 p-7 sm:p-9">
                            <div className="text-indigo-300/90 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we do promise</div>
                            <ul className="space-y-6">
                                {will.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <CheckCircle2 className="w-5 h-5 text-indigo-300 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="max-w-3xl mx-auto mt-10 rounded-2xl border border-amber-500/30 bg-amber-900/10 p-6 sm:p-8 text-center">
                        <div className="flex items-center justify-center gap-2 mb-3">
                            <Landmark className="w-5 h-5 text-amber-300" />
                            <span className="text-amber-300/90 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase">Your maths</span>
                        </div>
                        <p className="text-sm sm:text-base text-white/70 font-light leading-relaxed">
                            Six mentorship seats across the 90 days cover the entire engine.
                            One investment-tier client covers years of it.
                        </p>
                    </div>
                </section>

                {/* INVESTMENT — final section */}
                <section>
                    <div className="text-center mb-10 sm:mb-14">
                        <SectionEyebrow color="indigo">The investment</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Everything, one number.
                        </h2>
                    </div>

                    <div className="max-w-md mx-auto">
                        <div className="rounded-2xl border border-indigo-500/25 bg-white/5 backdrop-blur-sm px-7 py-8 text-left">
                            <div className="text-[10px] sm:text-xs text-white/40 font-bold tracking-widest uppercase mb-4 text-center">Your investment</div>
                            <div className="flex items-baseline justify-center gap-1.5 mb-6">
                                <span className="text-4xl sm:text-5xl font-black text-white tracking-tight">AED 10,000</span>
                                <span className="text-sm sm:text-base text-white/45 font-medium">/ month</span>
                            </div>
                            <ul className="space-y-3 border-t border-white/10 pt-5">
                                {[
                                    ['Videos', '60 a month · 2 a day'],
                                    ['Made with', 'Your AI clone · you approve every one'],
                                    ['Platforms', 'Instagram · YouTube · Facebook · TikTok'],
                                    ['Billing', 'Monthly in advance · 90-day initial term'],
                                    ['Guarantee', '10,000 followers or 100% back'],
                                    ['From you', '2h voice + 25min video, once']
                                ].map(([k, v]) => (
                                    <li key={k} className="flex items-start justify-between gap-4">
                                        <span className="text-xs sm:text-sm text-white/40 font-medium shrink-0">{k}</span>
                                        <span className="text-xs sm:text-sm text-white/80 font-semibold text-right">{v}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* CLOSING TAGLINE */}
                <section className="text-center pt-4 sm:pt-8">
                    <p className="text-slate-400 text-sm sm:text-base italic font-light">
                        Work less. Think bigger. Be seen.
                    </p>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-20 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
